import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import {
  CardHolder,
  CardHolderFilter,
  cardHolderAPI,
} from "../services/cardHolderAPI";
import { maskAadhar } from "./aadhar";

const EXPORT_PAGE_SIZE = 200;

export async function fetchAllCardHolders(
  filters: Omit<CardHolderFilter, "pageNo" | "pageSize">
): Promise<CardHolder[]> {
  const all: CardHolder[] = [];
  let pageNo = 1;
  let total = 0;

  do {
    const { items, total: count } = await cardHolderAPI.getCardHolders({
      ...filters,
      pageNo,
      pageSize: EXPORT_PAGE_SIZE,
    });
    total = count;
    all.push(...items);
    pageNo += 1;
  } while (all.length < total);

  return all;
}

type ExportRow = {
  sno: number;
  name: string;
  aadhar: string;
  phone: string;
  email: string;
  city: string;
  district: string;
  state: string;
  branch: string;
  status: string;
  createdAt: string;
};

function toExportRows(holders: CardHolder[]): ExportRow[] {
  return holders.map((h, index) => ({
    sno: index + 1,
    name: h.holderName || "—",
    aadhar: maskAadhar(h.aadharNumber) || "—",
    phone: h.phone || "—",
    email: h.email || "—",
    city: h.city || "—",
    district: h.district || "—",
    state: h.state || "—",
    branch: h.branch?.name || "—",
    status: h.isActive ? "Active" : "Inactive",
    createdAt: h.createdAt
      ? format(new Date(h.createdAt), "dd MMM yyyy")
      : "—",
  }));
}

function escapeCsvCell(value: string): string {
  const s = String(value ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

const HEADERS = [
  "S.No.",
  "Customer",
  "Aadhar",
  "Phone",
  "Email",
  "City",
  "District",
  "State",
  "Branch",
  "Status",
  "Created",
];

export function exportCardHoldersExcel(holders: CardHolder[]): void {
  const rows = toExportRows(holders);
  const lines = [
    HEADERS.join(","),
    ...rows.map((r) =>
      [
        r.sno,
        r.name,
        r.aadhar,
        r.phone,
        r.email,
        r.city,
        r.district,
        r.state,
        r.branch,
        r.status,
        r.createdAt,
      ]
        .map((v) => escapeCsvCell(String(v)))
        .join(",")
    ),
  ];
  const bom = "\uFEFF";
  const stamp = format(new Date(), "yyyy-MM-dd_HHmm");
  downloadBlob(
    new Blob([bom + lines.join("\n")], { type: "text/csv;charset=utf-8" }),
    `card-holders_${stamp}.csv`
  );
}

export function exportCardHoldersPdf(holders: CardHolder[]): void {
  const rows = toExportRows(holders);
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const stamp = format(new Date(), "dd MMM yyyy, HH:mm");

  doc.setFontSize(14);
  doc.text("Card Holders", 40, 36);
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Exported ${stamp} · ${holders.length} record(s)`, 40, 52);
  doc.setTextColor(0);

  autoTable(doc, {
    startY: 64,
    head: [HEADERS],
    body: rows.map((r) => [
      r.sno,
      r.name,
      r.aadhar,
      r.phone,
      r.email,
      r.city,
      r.district,
      r.state,
      r.branch,
      r.status,
      r.createdAt,
    ]),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: [41, 98, 255] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 24, right: 24 },
  });

  doc.save(`card-holders_${format(new Date(), "yyyy-MM-dd_HHmm")}.pdf`);
}
